import React from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ShieldAlert, ArrowRight } from 'lucide-react';

interface ModuleGuardProps {
  moduleId: string;
  children?: React.ReactNode;
}

export const ModuleGuard: React.FC<ModuleGuardProps> = ({ moduleId, children }) => {
  const { hasModule } = useAuth();
  const entitled = hasModule(moduleId);

  if (!entitled) {
    return (
      <div className="flex h-full min-h-[400px] w-full items-center justify-center p-6">
        <Card className="max-w-md border-amber-200 bg-amber-50/50 shadow-sm dark:border-amber-900 dark:bg-amber-950/20">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50">
              <ShieldAlert className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <CardTitle className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Module Not Enabled
            </CardTitle>
            <CardDescription className="text-sm text-zinc-600 dark:text-zinc-400">
              The module <span className="font-mono font-medium text-amber-700 dark:text-amber-300">{moduleId}</span> is not included in your organization's current active plan.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button asChild className="w-full">
              <Link to="/core/admin/modules-plans">
                Manage Modules & Plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-center text-xs text-zinc-500">
              Administrators can enable add-on modules and presets anytime in Settings.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return children ? <>{children}</> : <Outlet />;
};
